import React, { useContext, useMemo } from "react";
import { QueryKey, useInfiniteQuery } from "react-query";
import { useHistory, useRouteMatch } from "react-router-dom";
import { CellClickedEvent } from "@ag-grid-community/core";

import { PermissionLevel } from "fe-shared/src/types/domain";
import {
  DataGridFilters,
  DataGridOptions,
} from "fe-shared/src/components/DataGrid/modules/types";
import { hasPermission } from "fe-shared/src/helpers/permissions";
import useStorage from "fe-shared/src/hooks/useStorage";
import { getInvoicesRequestParams } from "fe-shared-transactions/src/helpers/invoicesRequestConverter";

import InvoicesDataGrid from "components/InvoicesDataGrid";
import UserContext from "contexts/userContext";

import { INVOICES_QUERY_KEY, PAGE_SIZE, PERMISSIONS } from "consts";

import { GridInvoiceList, GridInvoice } from "fe-shared-transactions/src/types";

import * as api from "./modules/api";

interface Props {
  lastOpenedInvoiceNumber: string | null;
  clearLastOpenedInvoiceNumber: () => void;
}

const Invoices = ({
  lastOpenedInvoiceNumber,
  clearLastOpenedInvoiceNumber,
}: Props) => {
  const user = useContext(UserContext);
  const history = useHistory();
  const match = useRouteMatch();
  const [options, setOptions] = useStorage<DataGridOptions>(
    "portalInvoicesOptions",
    {
      sortDirection: "desc",
      sortField: "submittedDate",
    },
    true
  );
  const [filters, setFilters] = useStorage<DataGridFilters>(
    "portalInvoicesFilters",
    {},
    true
  );

  const canViewList = hasPermission(user?.permissions, {
    name: PERMISSIONS.invoicesList,
    level: PermissionLevel.Read,
  });

  const canExport = hasPermission(user?.permissions, {
    name: PERMISSIONS.invoicesListExport,
    level: PermissionLevel.Full,
  });

  const canAssign = hasPermission(user?.permissions, {
    name: PERMISSIONS.invoiceAssign,
    level: PermissionLevel.Full,
  });

  const canViewDetails = hasPermission(user?.permissions, {
    name: PERMISSIONS.invoiceDetails,
    level: PermissionLevel.Read,
  });

  const requestParams = useMemo(
    () => getInvoicesRequestParams(filters, options),
    [filters, options]
  );

  const invoicesQueryKey: QueryKey = [INVOICES_QUERY_KEY, requestParams];

  const invoices = useInfiniteQuery<GridInvoiceList<GridInvoice>, unknown>(
    invoicesQueryKey,
    api.getInvoices,
    {
      enabled: canViewList,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      getFetchMore: (lastGroup, allGroups) =>
        allGroups.length * PAGE_SIZE < lastGroup.total
          ? allGroups.length + 1
          : undefined,
    }
  );

  const onCellClicked = (e: CellClickedEvent) => {
    const parentNode = (e.event?.target as Element).parentNode?.nodeName || "";
    const isButtonClicked = parentNode === "BUTTON";
    const colId = e.column.getColId();

    if (canViewDetails && !isButtonClicked && colId !== "assignedToName") {
      history.push(`${match.url}/${e.data.invoiceNumber}?navigation=true`);
    }
  };

  return canViewList ? (
    <InvoicesDataGrid
      invoices={invoices}
      lastOpenedInvoiceNumber={lastOpenedInvoiceNumber}
      clearLastOpenedInvoiceNumber={clearLastOpenedInvoiceNumber}
      options={options}
      filters={filters}
      setOptions={setOptions}
      setFilters={setFilters}
      canExport={canExport}
      canAssign={canAssign}
      invoicesQueryKey={invoicesQueryKey}
      onCellClicked={onCellClicked}
    />
  ) : null;
};

export default Invoices;
